import { ChartDataPoint } from './types';
import { Platform } from 'react-native';
import queryString from 'query-string';
import { getListItems } from '../func';
import { get } from 'lodash';
import { ICharts } from '@nocode/types';

const randomIDGenerator = (seed: string) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return () => {
    hash = Math.sin(hash) * 5000;
    return hash - Math.floor(hash);
  };
};

const generateMockValue = (
  fieldId: string,
  index: number,
  baseValue: number = 50,
  chartWidth?: number,
  chartHeight?: number
) => {
  const rng = randomIDGenerator(fieldId + index);
  const variance = baseValue * 0.4; // 40% variance

  // Adjust variance based on chart size for more responsive data
  let sizeMultiplier = 1;
  if (chartWidth && chartHeight) {
    // Increase data range for larger charts
    sizeMultiplier = Math.min(2, (chartWidth * chartHeight) / (300 * 200));
  }

  return Math.round(baseValue + (rng() - 0.5) * variance * 2 * sizeMultiplier);
};

// Generate date labels for mock data with 5/23 format
const generateDateLabels = (
  daysOffset: number = -6,
  count: number = 7,
  maxCount: number = 10,
  forceWeekView: boolean = false,
  periodFilter: 'weekly' | 'monthly' = 'weekly',
  navigationOffset: number = 0
) => {
  const labels = [];
  const today = new Date();

  // Determine the count based on period filter
  const isWeekly = periodFilter === 'weekly';
  const actualCount = isWeekly ? 7 : 30; // Always use 30 for monthly, ignore maxCount for monthly

  // Calculate the starting date based on navigation offset
  const startingDaysOffset = isWeekly
    ? -6 + navigationOffset
    : -6 + navigationOffset - 23;

  const labelStep = isWeekly ? 1 : 6;

  for (let i = 0; i < actualCount; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + startingDaysOffset + i);

    // Always add the date string, we'll handle label spacing in the chart rendering
    labels.push(`${date.getMonth() + 1}/${date.getDate()}`);
  }

  // For monthly view, selectively hide labels by replacing with empty strings
  if (!isWeekly) {
    for (let i = 0; i < labels.length; i++) {
      // Only show labels at intervals to prevent overlap
      if (i % labelStep !== 0 && i !== labels.length - 1) {
        labels[i] = '';
      }
    }
  }

  return labels;
};

const isDateField = (fieldLabel: string, fieldBinding: any) => {
  if (!fieldLabel && !fieldBinding) return false;

  const dateKeywords = ['date', 'day', 'ago', 'time', 'week', 'month', 'year'];
  const labelCheck =
    fieldLabel &&
    dateKeywords.some((keyword) => fieldLabel.toLowerCase().includes(keyword));

  const bindingCheck =
    fieldBinding &&
    (fieldBinding.source?.dataType === 'date' ||
      fieldBinding.source?.dataType === 'dateOnly' ||
      fieldBinding.source?.type === 'dateTime' ||
      fieldBinding.source?.options?.offset !== undefined);

  return labelCheck || bindingCheck;
};

// Function to check if a date field should have automatic 1-week filter applied
const shouldApplyWeekFilter = (fieldBinding: any) => {
  if (!fieldBinding || !fieldBinding.source) return false;

  // Check if it's a date field and doesn't already have a filter with offset
  const isDate =
    fieldBinding.source.dataType === 'date' ||
    fieldBinding.source.dataType === 'dateOnly' ||
    fieldBinding.source.type === 'dateTime';

  // Only apply if there's no existing offset configuration
  const hasExistingOffset = fieldBinding.source?.options?.offset !== undefined;

  return isDate && !hasExistingOffset;
};

// Function to check if field is a number field
const isNumberField = (fieldBinding: any) => {
  return (
    fieldBinding &&
    fieldBinding.source &&
    (fieldBinding.source.dataType === 'number' ||
      fieldBinding.source.type === 'number')
  );
};

// Function to apply 1-week ago filter to database options
const applyWeekFilterToOptions = (databaseOptions: any, fieldId: string) => {
  if (!databaseOptions || !fieldId) return databaseOptions;

  const updatedOptions = { ...databaseOptions };

  // Ensure filter array exists
  if (!updatedOptions.options) {
    updatedOptions.options = {};
  }
  if (!updatedOptions.options.filter) {
    updatedOptions.options.filter = [];
  }

  // Check if there's already a filter for this field
  const existingFilterIndex = updatedOptions.options.filter.findIndex(
    (filterGroup: any) =>
      filterGroup.some((filter: any) => filter.fieldId === fieldId)
  );

  // Create the 1-week ago filter (6 days ago to include today)
  const weekFilter = {
    id: `auto_week_filter_${fieldId}`,
    fieldId: fieldId,
    comparator: '>',
    comparison: {
      format: { type: 'relative' },
      type: 'dateTime',
      dataType: 'date',
      options: { offset: -6, startOfDay: true },
    },
  };

  if (existingFilterIndex >= 0) {
    // Update existing filter group
    const filterGroup = [...updatedOptions.options.filter[existingFilterIndex]];
    const existingFieldFilterIndex = filterGroup.findIndex(
      (filter: any) => filter.fieldId === fieldId
    );

    if (existingFieldFilterIndex >= 0) {
      filterGroup[existingFieldFilterIndex] = weekFilter;
    } else {
      filterGroup.push(weekFilter);
    }

    updatedOptions.options.filter[existingFilterIndex] = filterGroup;
  } else {
    // Add new filter group
    updatedOptions.options.filter.push([weekFilter]);
  }

  return updatedOptions;
};

// Generate distinct colors for chart fields
const generateFieldColor = (index: number, defaultColor?: string) => {
  if (defaultColor && index === 0) return defaultColor;

  const colorPalette = [
    '#0175ff',
    '#ff6b6b',
    '#4ecdc4',
    '#45b7d1',
    '#96c93f',
    '#ffb347',
    '#b347ff',
    '#ff4757',
    '#2ed573',
    '#ffa502',
    '#5352ed',
    '#ff3742',
    '#2f3542',
    '#3742fa',
    '#2ed573',
  ];

  if (index < colorPalette.length) {
    return colorPalette[index];
  }

  // Fallback to HSL for indices beyond the palette
  return `hsl(${(index * 137.508) % 360}, 70%, 50%)`;
};

export const getInitValues = (dataBinding: any, items: any) => {
  const search = queryString.parse(window?.location?.search);
  const target = search?.target;
  let len = 5;

  if (Platform.OS !== 'web') {
    return dataBinding;
  } else {
    if (target) {
      return dataBinding;
    } else {
      return getListItems(items, len);
    }
  }
};

export const getItemList = (attrs: any, _type?: string) => {
  const { dataBinding, attributes } = attrs;

  const columnCount = get(attributes, 'columnCount', 2);

  if (Platform.OS !== 'web') {
    // App
    return dataBinding || [];
  } else {
    // web
    const search = queryString.parse(window?.location?.search);
    const target = search?.target;

    if (target) {
      // preview
      return dataBinding || [];
    } else {
      // client

      const listData = getListItems(attributes.items, 4, columnCount);

      return listData;
    }
  }
};

export const mapDataToChartFormat = (props: any) => {
  const {
    attributes,
    dynamicFields,
    datasetMode,
    datasets,
    dataBinding,
    name,
    width,
    height,
    databaseOptions,
    componentData,
    periodFilter = 'weekly',
    dateOffset = 0,
  } = props;
  const { chartColor } = attributes;

  // Apply automatic 1-week filter for date fields
  let updatedDatabaseOptions = databaseOptions;

  // Check x-axis field
  const xAxisBinding = Array.isArray(attributes.xAxisField)
    ? attributes.xAxisField.find((item: any) => typeof item === 'object')
    : null;

  // Check y-axis field
  const yAxisBinding = Array.isArray(attributes.yAxisField)
    ? attributes.yAxisField.find((item: any) => typeof item === 'object')
    : null;

  const isYAxisDate =
    yAxisBinding &&
    (yAxisBinding.source?.dataType === 'date' ||
      yAxisBinding.source?.dataType === 'dateOnly' ||
      yAxisBinding.source?.type === 'dateTime');

  // Get maxValue from componentData first, then attributes for live data
  const resolvedMaxValueLive = componentData?.maxValue
    ? parseFloat(componentData.maxValue)
    : attributes.maxValue &&
      typeof attributes.maxValue === 'object' &&
      attributes.maxValue.parsedFormula
    ? parseFloat(attributes.maxValue.parsedFormula)
    : parseFloat(attributes.maxValue) || null;

  // Apply week filter for x-axis if it's a date field
  if (xAxisBinding && shouldApplyWeekFilter(xAxisBinding)) {
    updatedDatabaseOptions = applyWeekFilterToOptions(
      updatedDatabaseOptions,
      xAxisBinding.source?.fieldId
    );
  }

  // Apply week filter for y-axis if it's a date field
  if (yAxisBinding && shouldApplyWeekFilter(yAxisBinding)) {
    updatedDatabaseOptions = applyWeekFilterToOptions(
      updatedDatabaseOptions,
      yAxisBinding.source?.fieldId
    );
  }

  if (!dataBinding || !dataBinding.length) {
    return null;
  }

  const labels: any[] = [];
  const colors: any[] = [];
  const datasetsValue: any[] = [];

  if (dynamicFields && dynamicFields.length && datasetMode === 'single') {
    // Check if we have date fields and should use date labels instead of binding labels
    const hasDateField =
      (xAxisBinding && isDateField(xAxisBinding.label, xAxisBinding)) ||
      (yAxisBinding && isDateField(yAxisBinding.label, yAxisBinding));

    const labels = hasDateField
      ? generateDateLabels(-6, 7, 10, true, periodFilter, dateOffset) // Use actual date labels for date fields
      : dynamicFields.map((data: any) => data.label || 'Field'); // Use field labels for non-date fields

    const values = dynamicFields
      .map((data: any, _index: number) => {
        const value = dataBinding[0]?.[data.fieldId] ?? null;
        if (value != null) {
          return parseFloat(value);
        }
        return null;
      })
      .filter((data: any) => data != null);
    return {
      labels,
      colors: [chartColor],
      datasets: [
        {
          name: name,
          data: values,
          color: chartColor,
        },
      ],
      maxValue: resolvedMaxValueLive,
    };
  } else if (
    datasetMode === 'multiple' &&
    dynamicFields &&
    dynamicFields.length > 0
  ) {
    // Handle multiple dynamic fields
    // Check if we're dealing with date-based data for live charts
    const xAxisBinding = Array.isArray(attributes.xAxisField)
      ? attributes.xAxisField.find((item: any) => typeof item === 'object')
      : null;
    const isDateBasedLiveChart = isDateField(xAxisBinding?.label, xAxisBinding);

    // For live data, we need to handle the case where dataBinding might be empty or incomplete
    if (!dataBinding || !dataBinding.length) {
      // Return mock data structure similar to static data with responsive count
      const responsiveCount =
        width && height ? Math.min(10, Math.max(5, Math.floor(width / 50))) : 7;
      const mockLabels = isDateBasedLiveChart
        ? generateDateLabels(
            -6, // Start 6 days ago to include today
            7, // Always show 7 days
            10,
            true, // Force week view for date fields
            periodFilter,
            dateOffset
          )
        : ['Data Points'];

      dynamicFields.forEach((field: any, fieldIndex: number) => {
        const color =
          attributes[`${field.fieldId}-color`] ||
          generateFieldColor(fieldIndex, attributes.defaultFieldColor);
        colors.push(color);

        const dataPoints = isDateBasedLiveChart
          ? mockLabels.map((_, index) =>
              generateMockValue(
                field.fieldId,
                index,
                65 + fieldIndex * 15,
                width,
                height
              )
            )
          : [
              generateMockValue(
                field.fieldId,
                fieldIndex,
                50 + fieldIndex * 20,
                width,
                height
              ),
            ];

        datasetsValue.push({
          name: field.label || `Field ${fieldIndex + 1}`,
          data: dataPoints,
          color: color,
        });
      });

      return {
        labels: mockLabels,
        colors: colors,
        datasets: datasetsValue,
        maxValue: resolvedMaxValueLive,
      };
    }

    // Original logic for when we have actual data
    // Check if we have date fields and should use date labels instead of binding labels
    const hasDateField =
      (xAxisBinding && isDateField(xAxisBinding.label, xAxisBinding)) ||
      (yAxisBinding && isDateField(yAxisBinding.label, yAxisBinding));

    let fieldLabels;
    if (hasDateField) {
      // Use date labels for date fields
      fieldLabels = generateDateLabels(
        -6,
        7,
        10,
        true,
        periodFilter,
        dateOffset
      );
    } else {
      // Use field labels for non-date fields
      const allLabels = new Set<string>();
      dynamicFields.forEach((field: any) => {
        allLabels.add(field.label || 'Field');
      });
      fieldLabels = Array.from(allLabels);
    }

    dynamicFields.forEach((field: any, fieldIndex: number) => {
      const color =
        attributes[`${field.fieldId}-color`] ||
        generateFieldColor(fieldIndex, attributes.defaultFieldColor);
      colors.push(color);

      const fieldValue = dataBinding[0]?.[field.fieldId];
      const value =
        parseFloat(fieldValue) ||
        generateMockValue(field.fieldId, fieldIndex, 50, width, height);
      const dataArray = [value]; // Single value for each field
      datasetsValue.push({
        name: field.label || `Field ${fieldIndex + 1}`,
        data: dataArray,
        color: color,
      });
    });

    return {
      labels: fieldLabels,
      colors: colors,
      datasets: datasetsValue,
      maxValue: resolvedMaxValueLive,
    };
  } else if (
    datasetMode === 'multiplot' &&
    dynamicFields &&
    dynamicFields.length > 0
  ) {
    // Multi-plot logic: Create separate lines for each field in dynamicFields
    const colors: any[] = [];
    const datasetsValue: any[] = [];

    // Calculate responsive data limit based on width
    const dataLimit = width
      ? Math.min(dataBinding.length, Math.max(5, Math.floor(width / 50)))
      : dataBinding.length;

    // Filter to only show latest entry per day if x-axis is a date field
    let processedDataBinding = dataBinding;
    const xAxisBinding = Array.isArray(attributes.xAxisField)
      ? attributes.xAxisField.find((item: any) => typeof item === 'object')
      : null;
    const yAxisBinding = Array.isArray(attributes.yAxisField)
      ? attributes.yAxisField.find((item: any) => typeof item === 'object')
      : null;
    const isDateBasedXAxis = isDateField(xAxisBinding?.label, xAxisBinding);
    const isDateBasedYAxis = isDateField(yAxisBinding?.label, yAxisBinding);

    if (isDateBasedXAxis || isDateBasedYAxis) {
      // Group by date and keep only the latest entry for each day
      const dateGroups = new Map();

      dataBinding.forEach((item: any) => {
        let dateValue;
        if (isDateBasedXAxis) {
          dateValue = item.xAxisField || item[xAxisBinding?.source?.fieldId];
        } else if (isDateBasedYAxis) {
          dateValue = item.yAxisField || item[yAxisBinding?.source?.fieldId];
        }

        if (dateValue) {
          // Handle relative date strings vs actual dates
          let actualDate;
          try {
            // Try parsing as a regular date first
            actualDate = new Date(dateValue);
            // Check if the parsed date is invalid (relative strings like "19 hours ago")
            if (isNaN(actualDate.getTime())) {
              // For relative date strings, always use createdAt for grouping
              actualDate = new Date(item.createdAt);
            }
          } catch (e) {
            // Fall back to using createdAt for grouping
            actualDate = new Date(item.createdAt);
          }

          // Additional check: if dateValue is a relative string, use createdAt
          if (
            typeof dateValue === 'string' &&
            (dateValue.includes('ago') ||
              dateValue.includes('from now') ||
              dateValue.includes('hours') ||
              dateValue.includes('days'))
          ) {
            actualDate = new Date(item.createdAt);
          }

          const dateKey = actualDate.toDateString();
          const currentCreatedAt = new Date(item.createdAt);

          if (
            !dateGroups.has(dateKey) ||
            new Date(dateGroups.get(dateKey).createdAt) < currentCreatedAt
          ) {
            dateGroups.set(dateKey, item);
          }
        }
      });

      processedDataBinding = Array.from(dateGroups.values());
    }

    const limitedDataBinding = processedDataBinding.slice(0, dataLimit);

    let plotLabels: string[];

    if (isDateBasedXAxis || isDateBasedYAxis) {
      // For date-based X-axis or Y-axis, use actual date values from data if available
      if (
        processedDataBinding.length > 0 &&
        (isDateBasedXAxis || isDateBasedYAxis)
      ) {
        // Extract unique dates from actual data and sort them
        const uniqueDates = new Set<string>();
        processedDataBinding.forEach((item: any) => {
          let dateValue;
          if (isDateBasedXAxis) {
            dateValue = item.xAxisField || item[xAxisBinding?.source?.fieldId];
          } else if (isDateBasedYAxis) {
            dateValue = item.yAxisField || item[yAxisBinding?.source?.fieldId];
          }

          if (dateValue) {
            try {
              let date;
              if (typeof dateValue === 'string') {
                if (dateValue.includes('/')) {
                  const parts = dateValue.split('/');
                  if (parts.length === 3) {
                    date = new Date(
                      parseInt(parts[0]),
                      parseInt(parts[1]) - 1,
                      parseInt(parts[2])
                    );
                  } else {
                    date = new Date(dateValue);
                  }
                } else {
                  date = new Date(dateValue);
                }
              } else {
                date = new Date(dateValue);
              }

              if (!isNaN(date.getTime())) {
                uniqueDates.add(`${date.getMonth() + 1}/${date.getDate()}`);
              }
            } catch (e) {
              console.warn('Failed to parse date value:', dateValue, e);
            }
          }
        });

        // If we have actual date data, use it but ensure we have 7 days
        if (uniqueDates.size > 0) {
          const dateArray = Array.from(uniqueDates).sort((a, b) => {
            const [aMonth, aDay] = a.split('/').map(Number);
            const [bMonth, bDay] = b.split('/').map(Number);
            const aDate = new Date();
            aDate.setMonth(aMonth - 1, aDay);
            const bDate = new Date();
            bDate.setMonth(bMonth - 1, bDay);
            return aDate.getTime() - bDate.getTime();
          });

          // If we have fewer days than needed, fill in missing days
          const requiredDays = periodFilter === 'weekly' ? 7 : 30;
          if (dateArray.length < requiredDays) {
            plotLabels = generateDateLabels(
              -6,
              7,
              10,
              true,
              periodFilter,
              dateOffset
            );
          } else {
            plotLabels = dateArray.slice(0, requiredDays); // Take required number of dates
          }
        } else {
          plotLabels = generateDateLabels(
            -6,
            7,
            10,
            true,
            periodFilter,
            dateOffset
          );
        }
      } else {
        // Fallback to generated 7 days
        plotLabels = generateDateLabels(
          -6,
          7,
          10,
          true,
          periodFilter,
          dateOffset
        );
      }
    } else if (attributes.xAxisField && xAxisBinding) {
      // Use actual values from xAxisField - server has already resolved CURRENT_USER_SELECTOR
      plotLabels = limitedDataBinding.map((item: any) => {
        // For resolved bindings (including CURRENT_USER_SELECTOR), use the resolved xAxisField value directly
        const value = item.xAxisField || item[xAxisBinding.source?.fieldId];

        return value ? String(value) : 'N/A';
      });
    } else {
      // Fallback to sortedItemNumber or index
      plotLabels = limitedDataBinding.map((item: any, index: number) => {
        return item.sortedItemNumber
          ? `Point ${item.sortedItemNumber}`
          : `Point ${index + 1}`;
      });
    }

    // Create a dataset (line) for each dynamic field
    dynamicFields.forEach((field: any, fieldIndex: number) => {
      const color =
        attributes[`${field.fieldId}-color`] ||
        generateFieldColor(fieldIndex, attributes.defaultFieldColor);
      colors.push(color);

      let fieldData: number[];

      if (isDateBasedXAxis || isDateBasedYAxis) {
        // For date-based X-axis or Y-axis, create data for all days in the selected period
        fieldData = plotLabels.map((dateLabel, dayIndex) => {
          // For empty labels (monthly view spacing), calculate the actual date from the index
          let targetDate: Date;
          if (dateLabel === '') {
            // Calculate actual date from the starting offset and day index
            const isWeekly = periodFilter === 'weekly';
            const startingDaysOffset = isWeekly
              ? -6 + dateOffset
              : -6 + dateOffset - 23;
            targetDate = new Date();
            targetDate.setDate(
              targetDate.getDate() + startingDaysOffset + dayIndex
            );
          } else {
            // Convert date label back to date to match with data
            const [month, day] = dateLabel.split('/').map(Number);
            targetDate = new Date();
            targetDate.setMonth(month - 1, day);
          }

          const matchingItem = processedDataBinding.find((item: any) => {
            let itemDateValue =
              item.xAxisField || item[xAxisBinding?.source?.fieldId];
            if (itemDateValue) {
              try {
                let itemDate;
                if (
                  typeof itemDateValue === 'string' &&
                  itemDateValue.includes('/')
                ) {
                  const parts = itemDateValue.split('/');
                  if (parts.length === 3) {
                    // Handle YYYY/MM/DD format
                    itemDate = new Date(
                      parseInt(parts[0]),
                      parseInt(parts[1]) - 1,
                      parseInt(parts[2])
                    );
                  } else {
                    itemDate = new Date(itemDateValue);
                  }
                } else {
                  itemDate = new Date(itemDateValue);
                }

                if (!isNaN(itemDate.getTime())) {
                  return (
                    itemDate.getFullYear() === targetDate.getFullYear() &&
                    itemDate.getMonth() === targetDate.getMonth() &&
                    itemDate.getDate() === targetDate.getDate()
                  );
                }
              } catch (e) {}
            }

            // Fallback to matching by createdAt
            const itemDate = new Date(item.createdAt);
            return (
              itemDate.getFullYear() === targetDate.getFullYear() &&
              itemDate.getMonth() === targetDate.getMonth() &&
              itemDate.getDate() === targetDate.getDate()
            );
          });

          if (matchingItem) {
            const value = parseFloat(matchingItem[field.fieldId]) || 0;
            return value;
          } else {
            // No data for this date, return 0
            return 0;
          }
        });
      } else {
        // Non-date based, use original logic
        fieldData = limitedDataBinding.map((item: any, index: number) => {
          const value = parseFloat(item[field.fieldId]) || 0;
          return value;
        });
      }

      datasetsValue.push({
        name: field.label || field.fieldId || `Field ${fieldIndex + 1}`,
        data: fieldData,
        color: color,
      });
    });

    const resolvedXAxisLabel =
      limitedDataBinding.length > 0 ? limitedDataBinding[0].xAxisLabel : null;
    const resolvedYAxisLabel =
      limitedDataBinding.length > 0 ? limitedDataBinding[0].yAxisLabel : null;

    return {
      labels: plotLabels,
      colors: colors,
      datasets: datasetsValue,
      maxValue: resolvedMaxValueLive,

      resolvedAxisLabels: {
        x: resolvedXAxisLabel,
        y: resolvedYAxisLabel,
      },
    };
  } else if (datasetMode === 'multiple' && datasets && datasets.length) {
    datasets.forEach((dataset: any) => {
      colors.push(
        attributes[`${dataset.fieldId}-color`] ||
          generateFieldColor(0, attributes.defaultFieldColor)
      );
      const objectValues: any[] = [];
      datasets.forEach((dataset: any) => {
        if (dataset.fields && dataset.fields.length) {
          dataset.fields.forEach((field: any) => {
            objectValues.push({
              label: field.label || 'Field',
              value: dataBinding[0]?.[dataset.fieldId],
            });
          });
        }
      });

      if (dataset.useOwnValues) {
        const fieldValue = dataBinding[0]?.[dataset.fieldId];
        const value = parseFloat(fieldValue) || 0;
        const repeatedData = Array(labels.length).fill(value);
        datasetsValue.push({
          name: dataset.label,
          data: repeatedData,
          color:
            attributes[`${dataset.fieldId}-color`] ||
            generateFieldColor(
              datasets.indexOf(dataset),
              attributes.defaultFieldColor
            ),
        });
        // labels.push(dataset.label);
      } else {
        const fieldData =
          dataset.fields?.map((field: any) => {
            const fieldValue = dataBinding[0]?.[field.fieldId];
            return parseFloat(fieldValue) || 0;
          }) || [];

        datasetsValue.push({
          name: dataset.label,
          data: fieldData,
          color:
            attributes[`${dataset.fieldId}-color`] ||
            generateFieldColor(
              datasets.indexOf(dataset),
              attributes.defaultFieldColor
            ),
        });

        // Add field labels if not already added
        if (labels.length === 0) {
          dataset.fields?.forEach((field: any) => {
            labels.push(field.label);
          });
        }
      }
    });
  }

  return {
    labels,
    colors,
    datasets: datasetsValue,
    maxValue: resolvedMaxValueLive,
  };
};

// Helper function to detect if a binding is date-based
const isDateBasedBinding = (binding: any) => {
  return (
    binding &&
    (isDateField(binding?.label, binding) ||
      binding.source?.dataType === 'date' ||
      binding.source?.dataType === 'dateOnly' ||
      binding.source?.type === 'dateTime')
  );
};

// Helper function to resolve maxValue from multiple sources
const resolveMaxValue = (componentData: any, attributes: any) => {
  return componentData?.maxValue
    ? parseFloat(componentData.maxValue)
    : attributes.maxValue &&
      typeof attributes.maxValue === 'object' &&
      attributes.maxValue.parsedFormula
    ? parseFloat(attributes.maxValue.parsedFormula)
    : parseFloat(attributes.maxValue) || null;
};

// Helper function to generate labels based on maxValue and date detection
const generateLabelsForChart = (
  resolvedMaxValue: number | null,
  isDateBased: boolean,
  periodFilter: 'weekly' | 'monthly' = 'weekly',
  dateOffset: number = 0
) => {
  if (resolvedMaxValue && resolvedMaxValue > 0) {
    const gridCount = Math.min(
      20,
      Math.max(5, Math.ceil(resolvedMaxValue / 20))
    );
    const stepValue = Math.ceil(resolvedMaxValue / gridCount);
    const labels = Array.from({ length: gridCount }, (_, i) =>
      String((i + 1) * stepValue)
    );
    // Override with date labels if it's a date-based field
    return isDateBased
      ? generateDateLabels(-6, 7, 10, true, periodFilter, dateOffset)
      : labels;
  } else if (isDateBased) {
    return generateDateLabels(-6, 7, 10, true, periodFilter, dateOffset);
  } else {
    return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'];
  }
};

export const useStaticChartData = (props: any) => {
  const {
    attributes,
    dynamicFields,
    datasetMode,
    name,
    width,
    height,
    componentData,
    periodFilter = 'weekly',
    dateOffset = 0,
  } = props;
  const { chartColor, xAxisField } = attributes;
  const { datasets } = props;

  // Extract axis bindings
  const xAxisBinding = Array.isArray(xAxisField)
    ? xAxisField.find((item: any) => typeof item === 'object')
    : null;
  const yAxisBinding = Array.isArray(attributes.yAxisField)
    ? attributes.yAxisField.find((item: any) => typeof item === 'object')
    : null;

  // Check if we're dealing with date-based data
  const isDateBasedChart = isDateBasedBinding(xAxisBinding);
  const isYAxisDateBased = isDateBasedBinding(yAxisBinding);
  const isAnyAxisDateBased = isDateBasedChart || isYAxisDateBased;

  // Resolve maxValue once
  const resolvedMaxValueStatic = resolveMaxValue(componentData, attributes);

  // Generate appropriate labels based on maxValue and date detection
  const defaultLabels = generateLabelsForChart(
    resolvedMaxValueStatic,
    isAnyAxisDateBased,
    periodFilter,
    dateOffset
  );

  if (dynamicFields && dynamicFields.length && datasetMode === 'single') {
    let labels, values;
    if (
      isAnyAxisDateBased ||
      (resolvedMaxValueStatic && resolvedMaxValueStatic > 0)
    ) {
      labels = defaultLabels;
      const baseValue = resolvedMaxValueStatic
        ? resolvedMaxValueStatic * 0.7
        : 75;
      values = defaultLabels.map((_, index) =>
        generateMockValue(name || 'single', index, baseValue, width, height)
      );
    } else {
      labels = dynamicFields.map((data: any) => data.label || 'Field');
      values = dynamicFields.map((data: any, index: number) =>
        generateMockValue(data.fieldId, index, 50, width, height)
      );
    }

    return {
      labels,
      colors: [chartColor],
      datasets: [
        {
          name: name || 'Dataset',
          data: values,
          color: chartColor,
        },
      ],
    };
  }

  if (
    dynamicFields &&
    dynamicFields.length > 0 &&
    (datasetMode === 'multiple' || datasetMode === 'multiplot')
  ) {
    // Handle multiple dynamic fields for static data
    const colors: any[] = [];
    const datasetsValue: any[] = [];
    const resolvedMaxValue = resolvedMaxValueStatic;

    dynamicFields.forEach((field: any, fieldIndex: number) => {
      const color =
        attributes[`${field.fieldId}-color`] ||
        generateFieldColor(fieldIndex, attributes.defaultFieldColor);
      colors.push(color);

      // Generate data series for each field
      const baseValue = isAnyAxisDateBased
        ? 65 + fieldIndex * 15
        : 50 + fieldIndex * 20;
      const dataPoints =
        datasetMode === 'multiplot' || isAnyAxisDateBased
          ? defaultLabels.map((_, index) =>
              generateMockValue(field.fieldId, index, baseValue, width, height)
            )
          : [
              generateMockValue(
                field.fieldId,
                fieldIndex,
                baseValue,
                width,
                height
              ),
            ];

      datasetsValue.push({
        name: field.label || `Field ${fieldIndex + 1}`,
        data: dataPoints,
        color: color,
      });
    });

    const finalLabels =
      (resolvedMaxValue && resolvedMaxValue > 0) ||
      isAnyAxisDateBased ||
      datasetMode === 'multiplot'
        ? generateLabelsForChart(
            resolvedMaxValue,
            isAnyAxisDateBased,
            periodFilter,
            dateOffset
          )
        : ['Data Points'];
    return {
      labels: finalLabels,
      colors: colors,
      datasets: datasetsValue,
      maxValue: resolvedMaxValueStatic,
    };
  }

  if (datasets && datasets.length && datasetMode === 'multiple') {
    const colors: any[] = [];
    const datasetsValue: any[] = [];

    // Get all unique labels from all datasets
    const allLabels = new Set<string>();
    datasets.forEach((dataset: any) => {
      if (dataset.fields && dataset.fields.length) {
        dataset.fields.forEach((field: any) => {
          allLabels.add(field.label || 'Field');
        });
      }
    });

    const labels = Array.from(allLabels);
    datasets.forEach((dataset: any, datasetIndex: number) => {
      const color =
        attributes[`${dataset.fieldId}-color`] ||
        generateFieldColor(datasetIndex, attributes.defaultFieldColor);
      colors.push(color);

      let dataArray: number[] = [];
      if (dataset.useOwnValues) {
        dataArray = Array(labels.length).fill(20 + datasetIndex * 2);
      } else if (
        dataset.fields &&
        dataset.fields.length &&
        !dataset.useOwnValues
      ) {
        dataArray = dataset.fields.map(
          (_: any, fieldIndex: number) => 15 * fieldIndex + (fieldIndex % 2)
        );
      }
      const newDataSet = {
        name: dataset.label || `Dataset ${datasetIndex + 1}`,
        data: dataArray,
        color: color,
      };

      datasetsValue.push(newDataSet);
    });

    return {
      labels: labels.length > 0 ? labels : defaultLabels,
      colors,
      datasets: datasetsValue,
      maxValue: resolvedMaxValueStatic,
    };
  }

  if (datasetMode === 'multiplot') {
    const colors: any[] = [];
    const datasetsValue: any[] = [];
    const resolvedMaxValue = resolvedMaxValueStatic;
    const isDateBasedXAxis = isDateBasedChart;
    const isDateBasedYAxis = isYAxisDateBased;

    const responsiveLabelCount =
      !resolvedMaxValue && !isDateBasedXAxis && !isDateBasedYAxis
        ? width && height
          ? Math.min(20, Math.max(5, Math.floor(width / 30)))
          : 10
        : 10;

    let plotLabels: string[];

    if (isDateBasedXAxis || isDateBasedYAxis) {
      plotLabels = generateDateLabels(
        -6,
        7,
        10,
        true,
        periodFilter,
        dateOffset
      );
    } else if (!xAxisBinding) {
      plotLabels = Array.from({ length: responsiveLabelCount }, (_, i) => {
        const date = new Date();
        date.setDate(date.getDate() - 30 + i * 2);
        return `${date.getMonth() + 1}/${date.getDate()}`;
      });
    } else if (
      isNumberField(xAxisBinding) ||
      isNumberField(yAxisBinding) ||
      (resolvedMaxValue && resolvedMaxValue > 0)
    ) {
      const gridCount =
        resolvedMaxValue && resolvedMaxValue > 0
          ? Math.min(20, Math.max(5, Math.ceil(resolvedMaxValue / 20)))
          : Math.min(10, Math.max(5, responsiveLabelCount));
      const stepValue =
        resolvedMaxValue && resolvedMaxValue > 0
          ? Math.ceil(resolvedMaxValue / gridCount)
          : 10;
      plotLabels = Array.from({ length: gridCount }, (_, i) =>
        String((i + 1) * stepValue)
      );
    } else {
      const baseValue = 10;
      plotLabels = Array.from({ length: responsiveLabelCount }, (_, i) =>
        String(baseValue + i * 10)
      );
    }

    if (dynamicFields && dynamicFields.length > 0) {
      dynamicFields.forEach((field: any, fieldIndex: number) => {
        const color =
          attributes[`${field.fieldId}-color`] ||
          generateFieldColor(fieldIndex, attributes.defaultFieldColor);
        colors.push(color);

        const baseValue = 10 + fieldIndex * 10;
        const fieldData = plotLabels.map((_, index) =>
          generateMockValue(field.fieldId, index, baseValue, width, height)
        );

        datasetsValue.push({
          name: field.label || field.fieldId || `Field ${fieldIndex + 1}`,
          data: fieldData,
          color: color,
        });
      });
    } else {
      const baseColors = [
        '#007AFF',
        '#FF3B30',
        '#FFCC00',
        '#34C759',
        '#FF9500',
        '#AF52DE',
      ];

      const mockDatasets = [
        { name: 'Series 1', baseValue: 60 },
        { name: 'Series 2', baseValue: 40 },
        { name: 'Series 3', baseValue: 80 },
      ];

      mockDatasets.forEach((mockDataset, index) => {
        const color = baseColors[index % baseColors.length];
        colors.push(color);

        const fieldData = plotLabels.map((_, dataIndex) =>
          generateMockValue(
            `mock-${index}`,
            dataIndex,
            mockDataset.baseValue,
            width,
            height
          )
        );

        datasetsValue.push({
          name: mockDataset.name,
          data: fieldData,
          color: color,
        });
      });
    }

    return {
      labels: plotLabels,
      colors: colors,
      datasets: datasetsValue,
      maxValue: resolvedMaxValueStatic,
    };
  }

  // Fallback static data
  return {
    labels: defaultLabels,
    colors: [chartColor],
    datasets: [
      {
        data: defaultLabels.map((_, index) =>
          generateMockValue('fallback', index, 60, width, height)
        ),
        color: chartColor,
        strokeWidth: 2,
        name: name || 'Default Dataset',
      },
    ],
    maxValue: resolvedMaxValueStatic,
  };
};

export const mapChartData = (
  dataBinding: ChartDataPoint[],
  chartType: 'bar' | 'line' | 'pie' | 'radar'
) => {
  const labels = dataBinding.map((item) => item.label);
  const colors = dataBinding.map((item) => item.color);
  const values = dataBinding.map((item) => item.value);

  if (chartType === 'pie' || chartType === 'radar') {
    return {
      labels,
      colors,
      datasets: [
        {
          data: values,
        },
      ],
    };
  }

  return {
    labels,
    colors,
    datasets: [
      {
        data: values,
        color: (opacity = 1) => `rgba(134, 65, 244, ${opacity})`,
        strokeWidth: 2,
      },
    ],
  };
};

export const allValuesZero = (data: any) => {
  if (!data?.datasets?.[0]?.data) return true;
  return data.datasets[0].data.every((value: number) => value === 0);
};

export const useChartValue = (props: ICharts) => {
  const { changeInput, setDefaultValue } = props;
  const search = queryString.parse(window?.location?.search);
  const isCanvas = !search?.target;

  const setChartValue = (value: string | number) => {
    if (isCanvas) {
      return;
    }
    changeInput?.(value);
    setDefaultValue?.(value);
  };

  return {
    setChartValue,
  };
};
